/**
 * `TransferModal` — the presentational "Transfer Case" form. Collects a committed
 * recipient, one-or-more reasons, an optional note and an optional case-context note, shows the
 * read-only "Logged As" line, and calls `onSubmit(payload)`. Owns FORM state ONLY — no api/context/
 * clock; the container (`TransferAction`) owns open/close, the actual call, submit status, and the
 * `loggedAs` values passed in as props. Mirrors `HoldModal` (CDMX-18412) deliberately.
 *
 * ── The preview state (the whole reason this modal looks unusual) ──────────────────────────────
 * There is NO backend that can honour a transfer: the existing reassignment path carries assignee
 * metadata only — no reason, no note, no case context — and no transfer audit record or history
 * surface exists (ticket AC11/AC12). So when the host supplies no `transferCase` capability
 * (`canSubmit === false`):
 * a PREVIEW NOTICE renders from the moment the modal opens, before anything is typed, so nobody
 *     writes a long note under a false premise;
 * the primary action renders VISIBLE BUT DISABLED with a hover/focus popover explaining why —
 *     the same affordance CDMX-18412 shipped for its unavailable "Hold & load next".
 * Validation is therefore exercised through the enabled path (stories, unit tests, and the future
 * wired adapter), not by clicking a disabled button.
 *
 * `buildTransferPayload` (`../../slots/buildTransferPayload`) is the single source of truth for
 * validation, reused for both the submit handler and the per-field error flags below. Field errors
 * are LIVE-DERIVED every render from the CURRENT values once a submit has been attempted, so
 * editing a field clears its error immediately.
 *
 * `ModalBody` holds ONLY the description: Chakra auto-wires the dialog's `aria-describedby` to
 * `ModalBody`'s id, so the actual form renders in a sibling `Box` — otherwise the whole form would
 * become the dialog's accessible description (same trick as `HoldModal`).
 */
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Textarea
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { RoundedButton } from '@codametrix/ui-components';

import type {
  TransferActionData,
  TransferPayload,
  TransferReasonKey,
  TransferRecipient
} from '../../interface/transfer';
import {
  buildTransferPayload,
  type TransferValidationError
} from '../../slots/buildTransferPayload';
import { CASE_ACTION_RADIUS, TRANSFER_COLORS } from '../../styles/tokens';
import { LoggedAsLine } from '../CaseActionModal';
import { RecipientCombobox } from '../RecipientCombobox';

/** See `HoldModal`'s note: RoundedButton forwards `isLoading` at runtime but doesn't type it. */
const AtomButton = RoundedButton as React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { isLoading?: boolean }
>;

export type TransferSubmitState = 'idle' | 'transferring' | 'error';

export interface TransferModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly loggedAs: {
    readonly name: string;
    readonly at: Date;
    /** IANA zone — pins the footer timestamp for deterministic rendering. */
    readonly timeZone?: string;
  };
  readonly data?: TransferActionData;
  readonly onSearch?: (
    term: string
  ) => Promise<readonly TransferRecipient[]>;
  readonly onSubmit: (payload: TransferPayload) => Promise<void>;
  readonly submitState: TransferSubmitState;
  readonly errorMessage?: string;
  /**
   * Whether a transfer can actually be performed. `false` (the shipped default in the app today,
   * because no `transferCase` capability exists) renders the preview notice + a disabled primary.
   */
  readonly canSubmit?: boolean;
  readonly finalFocusRef?: React.RefObject<HTMLElement>;
  /** Forwarded to the combobox so tests can drive the debounce without fake timers. */
  readonly searchDebounceMs?: number;
}

/** The six ticket reasons, in ticket order. "Excluded Provider" is present even though the
 *  prototype screenshot omits it — the ticket is the source of truth for content. */
const REASONS: ReadonlyArray<{ key: TransferReasonKey; label: string }> = [
  { key: 'excludedProvider', label: 'Excluded provider' },
  { key: 'wrongQueue', label: 'Wrong queue' },
  { key: 'workloadBalancing', label: 'Workload balancing' },
  { key: 'outOfOffice', label: 'Out of office' },
  { key: 'escalation', label: 'Escalation' },
  { key: 'other', label: 'Other' }
];

const TRANSFER_MODAL_TITLE = 'Transfer Case';
/** Ticket R3. Deliberately does NOT promise a queue destination (we transfer to people only in this
 *  story) and does NOT repeat the prototype's "They'll see your note" — nothing persists a note. */
const TRANSFER_MODAL_DESCRIPTION =
  'This case will be reassigned to another person and removed from your active workload.';
const PREVIEW_NOTICE =
  "Preview only — transfers can't be completed yet. Nothing you enter is saved.";
const SUBMIT_DISABLED_MSG =
  'Transfers are not available yet: there is no backend that can save the reason, note or audit record.';
const OTHER_UNCONFIGURED_MSG = "Other reasons aren't configured yet.";

const EYEBROW = {
  textStyle: 'Subtitle 1',
  color: TRANSFER_COLORS.inkSoft
} as const;

/** One reason pill — a REAL native checkbox (multi-select semantics) with a styled visual capsule.
 *  The text lives inside the `<label>` so the input keeps an accessible name. */
const ReasonPill: React.FC<{
  readonly label: string;
  readonly testId: string;
  readonly isChecked: boolean;
  readonly isDisabled?: boolean;
  readonly onToggle: () => void;
}> = ({ label, testId, isChecked, isDisabled, onToggle }) => (
  <Box
    as="label"
    display="inline-flex"
    cursor={isDisabled ? 'not-allowed' : 'pointer'}
  >
    <input
      type="checkbox"
      data-testid={testId}
      checked={isChecked}
      disabled={isDisabled}
      onChange={onToggle}
      style={{
        position: 'absolute',
        opacity: 0,
        width: '1px',
        height: '1px',
        margin: '-1px',
        overflow: 'hidden'
      }}
    />
    <Box
      as="span"
      aria-hidden
      textStyle="Body 1"
      px="0.8125rem"
      py="0.4375rem"
      borderRadius="full"
      border="1px solid"
      borderColor={isChecked ? TRANSFER_COLORS.chipSelected : TRANSFER_COLORS.border}
      bg={isChecked ? TRANSFER_COLORS.chipSelected : 'white'}
      color={isChecked ? 'white' : TRANSFER_COLORS.inkSoft}
      opacity={isDisabled ? 0.5 : 1}
      fontWeight={isChecked ? 600 : 400}
      transition="background-color 160ms ease, border-color 160ms ease, color 160ms ease"
    >
      {label}
    </Box>
  </Box>
);

export const TransferModal: React.FC<TransferModalProps> = ({
  isOpen,
  onClose,
  loggedAs,
  data,
  onSearch,
  onSubmit,
  submitState,
  errorMessage,
  canSubmit = false,
  finalFocusRef,
  searchDebounceMs
}) => {
  const [selected, setSelected] = useState<TransferRecipient | undefined>(
    undefined
  );
  const [reasons, setReasons] = useState<readonly TransferReasonKey[]>([]);
  const [otherReasonId, setOtherReasonId] = useState<string | undefined>(
    undefined
  );
  const [note, setNote] = useState('');
  const [caseContext, setCaseContext] = useState('');
  const [hasAttempted, setHasAttempted] = useState(false);
  const reasonGroupRef = useRef<HTMLElement>(null);
  const recipientRef = useRef<HTMLDivElement>(null);

  const otherReasons = data?.otherReasons ?? [];
  const otherConfigured = otherReasons.length > 0;

  // Reset ALL form state on each closed->open transition. `TransferAction` also remounts this
  // component per open (`key={openGen}`); this is defense-in-depth for any other caller.
  useEffect(() => {
    if (isOpen) {
      setSelected(undefined);
      setReasons([]);
      setOtherReasonId(undefined);
      setNote('');
      setCaseContext('');
      setHasAttempted(false);
    }
  }, [isOpen]);

  const busy = submitState === 'transferring';
  const values = {
    selectedRecipientId: selected?.id,
    reasons,
    otherReasonId,
    note,
    caseContext
  };
  const liveResult = hasAttempted ? buildTransferPayload(values) : null;
  const errs: TransferValidationError[] =
    liveResult && liveResult.ok === false ? liveResult.errors : [];
  const recipientError = errs.includes('recipient-required');
  const reasonError = errs.includes('reason-required');
  const otherError = errs.includes('other-reason-required');

  const toggleReason = (key: TransferReasonKey): void => {
    setReasons((current) =>
      current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key]
    );
    // Deselecting Other drops its dictionary value so a stale id can never reach the payload.
    if (key === 'other' && reasons.includes('other')) setOtherReasonId(undefined);
  };

  const attempt = async (): Promise<void> => {
    setHasAttempted(true);
    const result = buildTransferPayload(values);
    if (result.ok === false) {
      // Focus the first invalid control: recipient -> reason group.
      if (result.errors.includes('recipient-required')) {
        recipientRef.current?.querySelector('input')?.focus();
      } else if (
        result.errors.includes('reason-required') ||
        result.errors.includes('other-reason-required')
      ) {
        reasonGroupRef.current?.focus();
      }
      return;
    }
    await onSubmit(result.payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={busy ? () => undefined : onClose}
      isCentered
      size="xl"
      finalFocusRef={finalFocusRef}
      closeOnOverlayClick={!busy}
      closeOnEsc={!busy}
    >
      <ModalOverlay />
      <ModalContent
        data-testid="transfer-modal"
        borderRadius={CASE_ACTION_RADIUS.modal}
        maxW="52rem"
        aria-label={TRANSFER_MODAL_TITLE}
      >
        <Flex
          align="center"
          gap={3}
          px={7}
          pt={6}
          pb={5}
          borderBottomWidth="1px"
          borderColor={TRANSFER_COLORS.border}
        >
          <Flex
            aria-hidden
            data-testid="transfer-modal-icon"
            bg={TRANSFER_COLORS.iconBg}
            borderRadius={CASE_ACTION_RADIUS.tile}
            boxSize="2.75rem"
            flexShrink={0}
            align="center"
            justify="center"
          >
            <Box
              as="svg"
              viewBox="0 0 24 24"
              width="1.4rem"
              height="1.4rem"
              fill="none"
              stroke={TRANSFER_COLORS.iconArrow}
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7 17 17 7M9 7h8v8" />
            </Box>
          </Flex>
          <Flex direction="column" justify="center" gap="0.125rem" minW={0}>
            <Text textStyle="h3">{TRANSFER_MODAL_TITLE}</Text>
            <ModalBody p={0}>
              <Text textStyle="Body 1" color={TRANSFER_COLORS.inkMuted}>
                {TRANSFER_MODAL_DESCRIPTION}
              </Text>
            </ModalBody>
          </Flex>
        </Flex>
        <ModalCloseButton
          isDisabled={busy}
          aria-label="Close"
          top="2.125rem"
          right={7}
          bg={TRANSFER_COLORS.inputBg}
          border={`1px solid ${TRANSFER_COLORS.border}`}
          borderRadius={CASE_ACTION_RADIUS.small}
          size="sm"
          color={TRANSFER_COLORS.inkMuted}
          _hover={{ bg: TRANSFER_COLORS.closeHoverBg }}
        />

        <Box px={7} pt={5} pb={5} maxH="60vh" overflowY="auto">
          {!canSubmit && (
            <Box
              data-testid="transfer-preview-notice"
              role="note"
              mb={5}
              px={4}
              py={3}
              bg={TRANSFER_COLORS.inputBg}
              border={`1px solid ${TRANSFER_COLORS.border}`}
              borderRadius={CASE_ACTION_RADIUS.control}
              textStyle="Body 1"
              color={TRANSFER_COLORS.inkSoft}
            >
              {PREVIEW_NOTICE}
            </Box>
          )}

          {/* PERSON */}
          <Box mb={6} ref={recipientRef}>
            <Text as="label" htmlFor="transfer-recipient-input" {...EYEBROW}>
              PERSON{' '}
              <Text as="span" color={TRANSFER_COLORS.error}>
                required
              </Text>
            </Text>
            <Box mt={1}>
              <RecipientCombobox
                initialRecipients={data?.initialRecipients}
                onSearch={onSearch}
                selected={selected}
                onSelect={setSelected}
                isDisabled={busy}
                isInvalid={recipientError}
                errorId="transfer-recipient-error"
                debounceMs={searchDebounceMs}
              />
            </Box>
            {recipientError && (
              <Text
                data-testid="transfer-recipient-error"
                id="transfer-recipient-error"
                role="alert"
                color={TRANSFER_COLORS.error}
                textStyle="Body 1"
                mt={1}
              >
                Select a person to transfer this case to.
              </Text>
            )}
          </Box>

          {/* REASON — native checkboxes in a real fieldset/legend */}
          <Box
            as="fieldset"
            ref={reasonGroupRef}
            tabIndex={-1}
            aria-invalid={reasonError || otherError || undefined}
            aria-describedby={
              reasonError
                ? 'transfer-reason-error'
                : otherError
                  ? 'transfer-other-error'
                  : undefined
            }
            mb={6}
          >
            <Text as="legend" {...EYEBROW} display="block" mb={3}>
              REASON{' '}
              <Text as="span" color={TRANSFER_COLORS.error}>
                required
              </Text>{' '}
              <Text as="span" color={TRANSFER_COLORS.inkFaint} fontWeight={400}>
                select all that apply
              </Text>
            </Text>
            <Flex wrap="wrap" gap={3}>
              {REASONS.map(({ key, label }) => {
                const pill = (
                  <ReasonPill
                    key={key}
                    testId={`transfer-reason-${key}`}
                    label={label}
                    isChecked={reasons.includes(key)}
                    isDisabled={busy || (key === 'other' && !otherConfigured)}
                    onToggle={() => toggleReason(key)}
                  />
                );
                // "Other" must reveal CONFIGURED dictionary values (ticket AC7). None exist yet, so
                // rather than let Other submit with no detail, it renders disabled and says why.
                return key === 'other' && !otherConfigured ? (
                  <Popover key={key} trigger="hover" placement="top" openDelay={120} isLazy>
                    <PopoverTrigger>
                      <Box
                        as="span"
                        data-testid="transfer-other-disabled"
                        aria-label="Other (unavailable)"
                        display="inline-flex"
                        tabIndex={0}
                        borderRadius="full"
                      >
                        {pill}
                      </Box>
                    </PopoverTrigger>
                    <PopoverContent
                      maxW="16.25rem"
                      bg={TRANSFER_COLORS.ink}
                      color="white"
                      border="none"
                      borderRadius={CASE_ACTION_RADIUS.control}
                      textStyle="Body 1"
                    >
                      <PopoverArrow bg={TRANSFER_COLORS.ink} />
                      <PopoverBody data-testid="transfer-other-disabled-popover">
                        {OTHER_UNCONFIGURED_MSG}
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                ) : (
                  pill
                );
              })}
            </Flex>
            {reasonError && (
              <Text
                data-testid="transfer-reason-error"
                id="transfer-reason-error"
                role="alert"
                color={TRANSFER_COLORS.error}
                textStyle="Body 1"
                mt={1}
              >
                Select at least one reason.
              </Text>
            )}

            {/* Other -> the configured dictionary values, single-select */}
            {reasons.includes('other') && otherConfigured && (
              <Box mt={3} data-testid="transfer-other-reasons">
                <Text {...EYEBROW} display="block" mb={2}>
                  OTHER REASON{' '}
                  <Text as="span" color={TRANSFER_COLORS.error}>
                    required
                  </Text>
                </Text>
                <Flex direction="column" gap={2}>
                  {otherReasons.map((reason) => (
                    <Box as="label" key={reason.id} display="inline-flex" gap={2} alignItems="center">
                      <input
                        type="radio"
                        name="transfer-other-reason"
                        data-testid={`transfer-other-reason-${reason.id}`}
                        checked={otherReasonId === reason.id}
                        disabled={busy}
                        onChange={() => setOtherReasonId(reason.id)}
                      />
                      <Text textStyle="Body 1" color={TRANSFER_COLORS.inkSoft}>
                        {reason.label}
                      </Text>
                    </Box>
                  ))}
                </Flex>
                {otherError && (
                  <Text
                    data-testid="transfer-other-error"
                    id="transfer-other-error"
                    role="alert"
                    color={TRANSFER_COLORS.error}
                    textStyle="Body 1"
                    mt={1}
                  >
                    Choose a specific reason.
                  </Text>
                )}
              </Box>
            )}
          </Box>

          {/* NOTE */}
          <Box mb={6}>
            <Text as="label" htmlFor="transfer-note-input" {...EYEBROW}>
              NOTE{' '}
              <Text as="span" color={TRANSFER_COLORS.inkFaint} fontWeight={400}>
                optional
              </Text>
            </Text>
            <Textarea
              id="transfer-note-input"
              data-testid="transfer-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              isDisabled={busy}
              placeholder="Add information about this transfer (optional)…"
              bg={TRANSFER_COLORS.inputBg}
              borderColor={TRANSFER_COLORS.border}
              borderRadius={CASE_ACTION_RADIUS.control}
              textStyle="Body 1"
              _hover={{ borderColor: TRANSFER_COLORS.noteHoverBorder }}
              _placeholder={{ color: TRANSFER_COLORS.inkFaint }}
              mt={1}
            />
          </Box>

          {/* ADD CONTEXT FOR THIS CASE — a SECOND, distinct optional field (ticket R7). The
              prototype merges the two; the ticket names them separately, so both ship. */}
          <Box>
            <Text as="label" htmlFor="transfer-context-input" {...EYEBROW}>
              ADD CONTEXT FOR THIS CASE{' '}
              <Text as="span" color={TRANSFER_COLORS.inkFaint} fontWeight={400}>
                optional
              </Text>
            </Text>
            <Textarea
              id="transfer-context-input"
              data-testid="transfer-context"
              value={caseContext}
              onChange={(e) => setCaseContext(e.target.value)}
              isDisabled={busy}
              placeholder="Add context that will help the receiving coder (optional)…"
              bg={TRANSFER_COLORS.inputBg}
              borderColor={TRANSFER_COLORS.border}
              borderRadius={CASE_ACTION_RADIUS.control}
              textStyle="Body 1"
              _hover={{ borderColor: TRANSFER_COLORS.noteHoverBorder }}
              _placeholder={{ color: TRANSFER_COLORS.inkFaint }}
              mt={1}
            />
          </Box>
        </Box>

        <ModalFooter
          alignItems="center"
          gap={4}
          px={7}
          sx={{ paddingTop: '1.125rem', paddingBottom: '1.125rem' }}
          borderTopWidth="1px"
          borderColor={TRANSFER_COLORS.border}
        >
          <LoggedAsLine
            testId="transfer-logged-as"
            name={loggedAs.name}
            at={loggedAs.at}
            timeZone={loggedAs.timeZone}
          />
          <Flex flex="1" gap={3} align="center" justify="flex-end" flexWrap="wrap">
            {submitState === 'error' && errorMessage && errs.length === 0 && (
              <Text
                data-testid="transfer-submit-error"
                role="alert"
                color={TRANSFER_COLORS.error}
                textStyle="Body 1"
              >
                {errorMessage}
              </Text>
            )}
            <AtomButton
              className="btn-secondary"
              data-testid="transfer-cancel"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </AtomButton>
            {canSubmit ? (
              <AtomButton
                className="btn-primary"
                data-testid="transfer-submit"
                onClick={() => attempt()}
                isLoading={busy}
                disabled={busy}
              >
                Transfer Case
              </AtomButton>
            ) : (
              // A disabled <button> emits no hover/focus events, so the explanatory popover hangs
              // off a focusable wrapper while the button itself is pointer-inert and out of the tab
              // order — the hint stays reachable by mouse AND keyboard (same shape as CDMX-18412's
              // unavailable "Hold & load next").
              <Popover trigger="hover" placement="top" openDelay={120} isLazy>
                <PopoverTrigger>
                  <Box
                    as="span"
                    data-testid="transfer-submit-disabled"
                    aria-label="Transfer Case (unavailable)"
                    display="inline-flex"
                    tabIndex={0}
                    borderRadius={CASE_ACTION_RADIUS.control}
                    _focusVisible={{ boxShadow: 'outline' }}
                  >
                    <AtomButton
                      className="btn-primary"
                      data-testid="transfer-submit"
                      disabled
                      tabIndex={-1}
                      style={{ pointerEvents: 'none' }}
                    >
                      Transfer Case
                    </AtomButton>
                  </Box>
                </PopoverTrigger>
                <PopoverContent
                  maxW="18rem"
                  bg={TRANSFER_COLORS.ink}
                  color="white"
                  border="none"
                  borderRadius={CASE_ACTION_RADIUS.control}
                  textStyle="Body 1"
                  lineHeight="1.45"
                >
                  <PopoverArrow bg={TRANSFER_COLORS.ink} />
                  <PopoverBody data-testid="transfer-submit-disabled-popover">
                    {SUBMIT_DISABLED_MSG}
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            )}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
